import { useEffect } from "react";
import { getShortcutKey } from "./platform";

/**
 * Predefined keyboard shortcuts for the application
 */
export const SHORTCUTS = {
  GENERAL: {
    TOGGLE_MENU: { key: "Escape", description: "Close menus/dialogs" },
    HELP: { key: "?", description: "Show keyboard shortcuts" },
  },
  DASHBOARD: {
    SHOW_ARCHIVED: { key: "a", description: "Toggle archived tasks" },
    ARCHIVE_DONE: { key: "d", description: "Archive done tasks" },
  },
  TASK: {
    EDIT: { key: "Enter", description: "Edit task" },
    SAVE: { key: "Ctrl+s", description: "Save changes" },
    CANCEL: { key: "Escape", description: "Cancel editing" },
    DELETE: { key: "Delete", description: "Delete task (when focused)" },
  },
  NAVIGATION: {
    DASHBOARD: { key: "g d", description: "Go to Dashboard" },
    INTAKE: { key: "g i", description: "Go to Intake" },
    WORKSHOP: { key: "g w", description: "Go to Workshop" },
    HELP: { key: "g h", description: "Go to Help" },
  },
};

/**
 * Format a shortcut key for display based on platform
 */
export const formatShortcut = (shortcut) => {
  if (!shortcut) return "";

  const formatted = getShortcutKey(shortcut);

  // Split combination shortcuts (e.g., "Ctrl+s" -> ["Ctrl", "s"])
  return formatted
    .split("+")
    .map((key) => {
      // Format special keys
      if (key === "Ctrl") return "Ctrl";
      if (key === "⌘") return "⌘";
      if (key === "Alt") return "Alt";
      if (key === "Shift") return "Shift";
      if (key === "Escape") return "Esc";
      if (key === "ArrowUp") return "↑";
      if (key === "ArrowDown") return "↓";
      if (key === "ArrowLeft") return "←";
      if (key === "ArrowRight") return "→";
      return key.toUpperCase();
    })
    .join(" + ");
};

/**
 * Hook to register global keyboard shortcuts
 *
 * @param {Object} shortcuts - Map of shortcut keys to handler functions
 * @param {boolean} enabled - Whether shortcuts are enabled
 */
export const useKeyboardShortcuts = (shortcuts, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    // Track keys currently being pressed for multi-key shortcuts
    let keysPressed = {};
    let keySequence = [];
    let sequenceTimer = null;

    function handleKeyDown(event) {
      // Don't trigger shortcuts when typing in input elements
      if (
        ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName) ||
        event.target.isContentEditable
      ) {
        return;
      }

      // Get normalized key name
      const key = event.key.toLowerCase();

      // Update keys pressed
      keysPressed[key] = true;

      // For sequence-based shortcuts (e.g., "g d")
      if (sequenceTimer) {
        clearTimeout(sequenceTimer);
      }

      keySequence.push(key);

      // Reset sequence after 500ms of inactivity
      sequenceTimer = setTimeout(() => {
        keySequence = [];
      }, 500);

      // Check all registered shortcuts
      for (const [shortcutKey, handler] of Object.entries(shortcuts)) {
        // Normalize the shortcut key for comparison
        const normalizedShortcut = shortcutKey.toLowerCase();

        // Check for combination shortcuts (e.g., "Ctrl+s")
        if (normalizedShortcut.includes("+")) {
          const keys = normalizedShortcut.split("+");
          const allKeysPressed = keys.every(
            (k) =>
              keysPressed[k.toLowerCase()] ||
              // Special case for modifier keys
              (k.toLowerCase() === "ctrl" &&
                (event.ctrlKey || event.metaKey)) ||
              (k.toLowerCase() === "alt" && event.altKey) ||
              (k.toLowerCase() === "shift" && event.shiftKey)
          );

          if (allKeysPressed) {
            event.preventDefault();
            handler(event);
          }
        }
        // Check for sequence shortcuts (e.g., "g d")
        else if (normalizedShortcut.includes(" ")) {
          const requiredSequence = normalizedShortcut.split(" ");
          const sequenceMatches = requiredSequence.every(
            (key, index) => keySequence[index] === key
          );

          if (
            sequenceMatches &&
            keySequence.length === requiredSequence.length
          ) {
            event.preventDefault();
            handler(event);
            keySequence = [];
          }
        }
        // Simple single key shortcuts
        else if (key === normalizedShortcut) {
          event.preventDefault();
          handler(event);
        }
      }
    }

    function handleKeyUp(event) {
      // Clear the key from the pressed keys
      delete keysPressed[event.key.toLowerCase()];
    }

    // Add event listeners
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [shortcuts, enabled]);
};

/**
 * Create a help modal component to display keyboard shortcuts
 */
export const getShortcutList = () => {
  const categories = Object.keys(SHORTCUTS);

  return categories.map((category) => {
    const shortcuts = Object.values(SHORTCUTS[category]);
    return {
      category,
      shortcuts: shortcuts.map((s) => ({
        key: formatShortcut(s.key),
        description: s.description,
      })),
    };
  });
};

export default {
  SHORTCUTS,
  formatShortcut,
  useKeyboardShortcuts,
  getShortcutList,
};
